/*
 * Copyright 2025 The Android Open Source Project
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     https://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import i18n.StringKey
import i18n.tr
import kotlinx.browser.document
import kotlinx.html.dom.create
import kotlinx.html.*
import kotlinx.html.js.*
import org.w3c.dom.*

class LoginComponent(
    private val isLoading: Boolean,
    private val errorMessage: String?,
    private val onLogin: (username: String, password: String) -> Unit,
    private val onCreateAccount: () -> Unit,
    private val onBackToLanding: () -> Unit = {},
    private val onForgotPassword: () -> Unit = {},
    private val onTryDemo: () -> Unit = {}
) {

    fun render(): HTMLElement {
        return document.create.div {
            style = """
                display: flex;
                flex-direction: column;
                align-items: center;
                justify-content: center;
                min-height: 100vh;
                font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
                background: linear-gradient(180deg, #0a0a0a 0%, #141418 100%);
                padding: 20px;
            """

            div {
                style = """
                    background: rgba(20, 20, 24, 0.9);
                    border-radius: 16px;
                    padding: 40px 32px;
                    box-shadow: 0 8px 32px rgba(0, 217, 255, 0.1), 0 0 0 1px rgba(255, 255, 255, 0.05);
                    width: 340px;
                    max-width: 90vw;
                    backdrop-filter: blur(10px);
                """

                // Logo image (clickable to return to landing)
                div {
                    style = """
                        display: flex;
                        justify-content: center;
                        align-items: center;
                        margin-bottom: 4px;
                    """
                    img {
                        id = "login-logo"
                        src = "/wiz3r_logo.png?v=3"
                        alt = "wiz≡r"
                        style = "height: 90px; cursor: pointer;"
                        onClickFunction = { onBackToLanding() }
                    }
                }

                // Tagline
                p {
                    style = """
                        text-align: center;
                        color: #666;
                        font-size: 14px;
                        margin-bottom: 32px;
                        margin-top: 4px;
                    """
                    +StringKey.APP_TAGLINE.tr()
                }
                
                // Wrap inputs in a form for proper keyboard handling
                form {
                    id = "login-form"
                    onSubmitFunction = { event ->
                        event.preventDefault()
                        val usernameInput = document.getElementById("username") as? HTMLInputElement
                        val passwordInput = document.getElementById("password") as? HTMLInputElement

                        val username = usernameInput?.value ?: ""
                        val password = passwordInput?.value ?: ""

                        if (username.isNotEmpty() && password.isNotEmpty() && !isLoading) {
                            onLogin(username, password)
                        }
                    }

                    div {
                        style = "margin-bottom: 16px;"
                        input {
                            type = InputType.email
                            placeholder = StringKey.AUTH_EMAIL_PLACEHOLDER.tr()
                            id = "username"
                            attributes["autocomplete"] = "email"
                            attributes["autocapitalize"] = "none"
                            attributes["spellcheck"] = "false"
                            autoFocus = true
                            style = """
                                width: 100%;
                                padding: 14px 16px;
                                border: 1px solid #333;
                                border-radius: 8px;
                                font-size: 16px;
                                box-sizing: border-box;
                                background-color: #1a1a1e;
                                color: #ffffff;
                                outline: none;
                                transition: border-color 0.2s, box-shadow 0.2s;
                            """
                        }
                    }

                    div {
                        style = "margin-bottom: 24px;"
                        input {
                            type = InputType.password
                            placeholder = "Password"
                            id = "password"
                            style = """
                                width: 100%;
                                padding: 14px 16px;
                                border: 1px solid #333;
                                border-radius: 8px;
                                font-size: 16px;
                                box-sizing: border-box;
                                background-color: #1a1a1e;
                                color: #ffffff;
                                outline: none;
                                transition: border-color 0.2s, box-shadow 0.2s;
                            """
                        }
                    }

                    // Forgot password link
                    div {
                        style = "text-align: right; margin-bottom: 16px; margin-top: -8px;"
                        a {
                            href = "#"
                            style = "color: #00d9ff; font-size: 13px; text-decoration: none;"
                            onClickFunction = { e ->
                                e.preventDefault()
                                onForgotPassword()
                            }
                            +"Forgot password?"
                        }
                    }

                    if (errorMessage != null) {
                        div {
                            style = """
                                color: #ff6b6b;
                                margin-bottom: 16px;
                                font-size: 14px;
                                text-align: center;
                                padding: 10px;
                                background: rgba(255, 107, 107, 0.1);
                                border-radius: 6px;
                            """
                            +errorMessage
                        }
                    }

                    // Submit button inside form - Enter key will trigger form submit
                    button {
                        type = ButtonType.submit
                        style = """
                            width: 100%;
                            padding: 14px;
                            background: linear-gradient(135deg, #00d9ff 0%, #00b8d9 100%);
                            color: #0a0a0a;
                            border: none;
                            border-radius: 8px;
                            font-size: 16px;
                            font-weight: 600;
                            cursor: pointer;
                            transition: transform 0.2s, box-shadow 0.2s;
                            box-shadow: 0 4px 12px rgba(0, 217, 255, 0.3);
                        """
                        id = "login-btn"
                        if (isLoading) {
                            disabled = true
                            style += "opacity: 0.7; cursor: not-allowed;"
                        }
                        if (isLoading) {
                            +StringKey.AUTH_SIGNING_IN.tr()
                        } else {
                            +StringKey.AUTH_SIGN_IN.tr()
                        }
                    }
                }

                // Divider
                div {
                    style = """
                        display: flex;
                        align-items: center;
                        margin: 20px 0;
                    """
                    span { style = "flex: 1; height: 1px; background: #333;" }
                    span {
                        style = "color: #666; font-size: 12px; padding: 0 12px;"
                        +"or"
                    }
                    span { style = "flex: 1; height: 1px; background: #333;" }
                }

                button {
                    style = """
                        width: 100%;
                        padding: 14px;
                        background-color: transparent;
                        color: #00d9ff;
                        border: 1px solid #00d9ff;
                        border-radius: 8px;
                        font-size: 16px;
                        font-weight: 500;
                        cursor: pointer;
                        transition: background-color 0.2s, color 0.2s;
                    """
                    onClickFunction = { event ->
                        event.preventDefault()
                        onCreateAccount()
                    }
                    +StringKey.AUTH_CREATE_ACCOUNT.tr()
                }

                // "Try it — no account" — a tinted, gently-pulsing pill with a
                // play glyph so the demo entry reads as an inviting CTA without
                // outranking the solid Sign In. Centered + auto-width keeps it
                // visually lighter (tertiary) than the two full-width buttons.
                div {
                    style = "display: flex; justify-content: center; margin-top: 18px;"
                    unsafe {
                        +"""<style>
                            @keyframes demoPulse {
                                0%, 100% { box-shadow: 0 0 0 0 rgba(0,217,255,0.0); }
                                50%      { box-shadow: 0 0 18px 2px rgba(0,217,255,0.30); }
                            }
                            #try-demo-btn:hover { background: rgba(0,217,255,0.18) !important; }
                            @media (prefers-reduced-motion: reduce) { #try-demo-btn { animation: none !important; } }
                        </style>"""
                    }
                    button {
                        id = "try-demo-btn"
                        style = """
                            display: inline-flex;
                            align-items: center;
                            gap: 7px;
                            padding: 8px 18px;
                            background: rgba(0, 217, 255, 0.10);
                            color: #00d9ff;
                            border: 1px solid rgba(0, 217, 255, 0.28);
                            border-radius: 999px;
                            font-size: 13.5px;
                            font-weight: 600;
                            cursor: pointer;
                            animation: demoPulse 2.8s ease-in-out infinite;
                            transition: background-color 0.2s;
                        """
                        if (isLoading) {
                            disabled = true
                            style += "opacity: 0.5; cursor: not-allowed; animation: none;"
                        }
                        onClickFunction = { event ->
                            event.preventDefault()
                            if (!isLoading) onTryDemo()
                        }
                        unsafe {
                            +"""<svg width="11" height="11" viewBox="0 0 24 24" fill="#00d9ff" style="flex-shrink:0;"><path d="M8 5v14l11-7z"/></svg>"""
                        }
                        span { +StringKey.AUTH_TRY_DEMO.tr() }
                    }
                }

                // Footer text
                p {
                    style = """
                        text-align: center;
                        color: #444;
                        font-size: 11px;
                        margin-top: 24px;
                        margin-bottom: 0;
                    """
                    +"Secure login powered by JWT authentication"
                }
            }
        }
    }
}
